import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import type { WithFieldValue } from "firebase/firestore";
import { firestore } from "./firebaseClient";

import {
  GLOBAL_LEGAL_DOC_ID,
  GLOBAL_SETTINGS_COLLECTION,
  type GlobalLegalDocument,
  type LegalSection,
} from "../models/legal";

const mapTimestamp = (value: unknown): Date | null => {
  if (value instanceof Timestamp) {
    return value.toDate();
  }
  return null;
};

export const getGlobalLegal = async (): Promise<GlobalLegalDocument | null> => {
  const ref = doc(firestore, GLOBAL_SETTINGS_COLLECTION, GLOBAL_LEGAL_DOC_ID);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    return null;
  }
  const data = snap.data() as Record<string, unknown>;
  const terms = (data.terms ?? {}) as Record<string, unknown>;
  const privacyPolicy = (data.privacyPolicy ?? {}) as Record<string, unknown>;

  const coerceSection = (section: Record<string, unknown>): LegalSection => ({
    es: typeof section.es === "string" ? section.es : "",
    en: typeof section.en === "string" ? section.en : "",
    version: typeof section.version === "string" ? section.version : "",
    updatedAt: mapTimestamp(section.updatedAt),
    updatedBy: typeof section.updatedBy === "string" ? section.updatedBy : null,
  });

  return {
    terms: coerceSection(terms),
    privacyPolicy: coerceSection(privacyPolicy),
    updatedAt: mapTimestamp(data.updatedAt),
  };
};

export const upsertGlobalLegal = async (input: {
  terms: Pick<LegalSection, "es" | "en" | "version">;
  privacyPolicy: Pick<LegalSection, "es" | "en" | "version">;
  updatedBy: string;
}): Promise<void> => {
  const ref = doc(firestore, GLOBAL_SETTINGS_COLLECTION, GLOBAL_LEGAL_DOC_ID);
  const payload = {
    terms: {
      ...input.terms,
      updatedAt: serverTimestamp(),
      updatedBy: input.updatedBy,
    },
    privacyPolicy: {
      ...input.privacyPolicy,
      updatedAt: serverTimestamp(),
      updatedBy: input.updatedBy,
    },
    updatedAt: serverTimestamp(),
  };

  // Create if missing, otherwise merge.
  await setDoc(ref, payload, { merge: true });
};

export const publishLegalUpdate = async (input: {
  patch: Partial<Pick<GlobalLegalDocument, "terms" | "privacyPolicy">>;
  updatedBy: string;
}): Promise<void> => {
  const ref = doc(firestore, GLOBAL_SETTINGS_COLLECTION, GLOBAL_LEGAL_DOC_ID);
  const patch: WithFieldValue<Partial<GlobalLegalDocument>> = {
    updatedAt: serverTimestamp(),
    ...(input.patch.terms
      ? {
          terms: {
            ...input.patch.terms,
            updatedAt: serverTimestamp(),
            updatedBy: input.updatedBy,
          },
        }
      : {}),
    ...(input.patch.privacyPolicy
      ? {
          privacyPolicy: {
            ...input.patch.privacyPolicy,
            updatedAt: serverTimestamp(),
            updatedBy: input.updatedBy,
          },
        }
      : {}),
  };

  // Merge patch into the existing global doc (creates if missing).
  await setDoc(ref, patch, { merge: true });
};
